import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../config/api';
import { applyBrandKitToDocument, resolveKitFromUser, PRESETS, SESSION_UPDATED_EVENT } from '../brandKits';
import { portalForHostname } from '../utils/portalExperience';

const BrandKitContext = createContext({
    kit: null,
    active: false,
    hideIpm: false,
    productName: 'IPM',
    portal: null,
});

function readUser() {
    try {
        return JSON.parse(localStorage.getItem('user') || 'null');
    } catch (_) {
        return null;
    }
}

export function BrandKitProvider({ children }) {
    const [user, setUser] = useState(readUser);
    const [remote, setRemote] = useState(undefined);

    useEffect(() => {
        const refresh = () => setUser(readUser());
        window.addEventListener('storage', refresh);
        window.addEventListener(SESSION_UPDATED_EVENT, refresh);
        const orig = localStorage.setItem.bind(localStorage);
        localStorage.setItem = (key, value) => {
            orig(key, value);
            if (key === 'user') refresh();
        };
        return () => {
            window.removeEventListener('storage', refresh);
            window.removeEventListener(SESSION_UPDATED_EVENT, refresh);
            localStorage.setItem = orig;
        };
    }, []);

    const userId = user?._id || user?.id || '';
    const token = user?.token || '';
    const remoteSessionKey = `${userId}|${token}`;
    const portal = portalForHostname();
    const portalKit = useMemo(() => portal ? { ...PRESETS[portal.kitId], productName: portal.productName } : null, [portal]);

    useEffect(() => {
        if (portalKit || !token) {
            setRemote(null);
            return undefined;
        }
        setRemote(undefined);
        let cancelled = false;
        api.get('/api/brand-kits')
            .then((res) => {
                if (cancelled) return;
                setRemote({
                    sessionKey: remoteSessionKey,
                    whiteLabel: res.data?.whiteLabel || null,
                    kit: res.data?.kit || null,
                });
            })
            .catch(() => {
                if (!cancelled) setRemote(null);
            });
        return () => { cancelled = true; };
    }, [userId, token, remoteSessionKey, portalKit]);

    const sessionKit = useMemo(() => resolveKitFromUser(user), [user]);
    const remoteEnabled = Boolean(userId && remote?.sessionKey === remoteSessionKey && remote.whiteLabel?.enabled && remote.kit);
    // Name/email Tyson seats (empty agencyId) resolve a kit even when stored whiteLabel.enabled is false.
    // Never let a failed/admin fetch (enabled:false) strip a kit already on the session.
    // The portal host owns presentation throughout the session. Account identity and
    // permissions stay on the session; its brand still applies on other hosts.
    const rawKit = portalKit || (userId ? (remoteEnabled ? remote.kit : sessionKit) : null);
    const kit = useMemo(() => {
        if (!rawKit) return null;
        const preset = PRESETS[rawKit.id];
        if (!preset) return rawKit;
        return {
            ...preset,
            ...rawKit,
            colors: { ...preset.colors, ...(rawKit.colors || {}) },
            logos: { ...preset.logos, ...(rawKit.logos || {}) },
            typography: { ...preset.typography, ...(rawKit.typography || {}) },
        };
    }, [rawKit]);
    const active = Boolean(kit);
    const hideIpm = Boolean(active && kit?.hideIpmBranding !== false);
    const productName = (active && kit?.productName) || 'IPM';
    useEffect(() => {
        applyBrandKitToDocument(kit);
        // No cleanup reset: navigation between branded pages keeps its theme.
    }, [kit]);

    const value = useMemo(
        () => ({ kit: active ? kit : null, active, hideIpm, productName, portal }),
        [kit, active, hideIpm, productName, portal],
    );

    return <BrandKitContext.Provider value={value}>{children}</BrandKitContext.Provider>;
}

export function useBrandKit() {
    return useContext(BrandKitContext);
}
